'use client'

import { Fragment, useMemo, useState } from 'react'
import Link from 'next/link'
import { useEnvContext } from 'next-runtime-env'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import {
  faChartLine,
  faList,
  faTag,
  faGear,
  faUserGroup,
  faSitemap,
  faChartBar,
} from '@document-crunch/pro-regular-svg-icons'

import { useAuth } from '@dc-shared-web/providers/AuthProvider'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@dc-shared-web/components/shadcn/dropdown-menu'
import { Button } from '@dc-shared-web/components/shadcn/button'

import { Tooltip } from '@dc-shared-web/components/base/Tooltip'
import { Icon, IconColor } from '@dc-shared-web/components/base/Icon'
import { useGetSelectedTeamData } from '@/app/core/hooks/useGetSelectedTeamData'
import { getTeamAdminTeamIds } from '@/app/helpers/userHelper'
import { useLDFlags } from '@/app/core/hooks/useLDFlags'
import { useFSTrackEvent } from '@/dc-shared-web/src'
import { FS } from '@/dc-shared-web/src/lib/trackingConstants'

type MenuItem = {
  icon: IconDefinition
  text: string
  includeSeparator: boolean
  link: string
  hidden: boolean
}

export default function AdminMenu() {
  const { user } = useAuth()
  const { NEXT_PUBLIC_DCRUNCH_API_URL } = useEnvContext()
  const linkPrefix = NEXT_PUBLIC_DCRUNCH_API_URL
  const [isOpen, setIsOpen] = useState(false)
  const selectedTeamData = useGetSelectedTeamData(user.teams)
  const {
    flags: { rd1203ExecutiveDashboard },
  } = useLDFlags()
  const { trackEvent: trackFSEvent } = useFSTrackEvent()

  const menuItems = useMemo(() => {
    const isSA = user?.is_super_admin
    const teamAdminTeamIds = getTeamAdminTeamIds(user)

    const isRoleAdmin = !isSA && teamAdminTeamIds.length > 0
    const selectedTeamUUID = selectedTeamData.id.toString()

    const menuItems: MenuItem[] = [
      {
        text: 'Manage teams',
        link: `/teams`,
        icon: faSitemap,
        hidden: !isSA,
        includeSeparator: false,
      },
      {
        text: 'Manage users',
        link: isRoleAdmin
          ? `/users?selectedTeamId=${selectedTeamUUID === '-1' ? teamAdminTeamIds[0] : selectedTeamUUID}`
          : `/users`,
        icon: faUserGroup,
        hidden: !isSA && !isRoleAdmin,
        includeSeparator: isSA,
      },
      {
        text: 'Customization',
        link: `${linkPrefix}/customizations`,
        icon: faList,
        hidden: !isSA,
        includeSeparator: false,
      },
      {
        text: 'User-refined provisions',
        link: `${linkPrefix}/annotations-action/list`,
        icon: faTag,
        hidden: !isSA,
        includeSeparator: true,
      },
      {
        text: 'Crunch report',
        link: `${linkPrefix}/reports/crunch/count`,
        icon: faChartLine,
        hidden: !isSA,
        includeSeparator: false,
      },
      {
        text: 'Crunch report by team',
        link: `${linkPrefix}/reports/crunch/count-by-team`,
        icon: faChartLine,
        hidden: !isSA,
        includeSeparator: rd1203ExecutiveDashboard,
      },
      {
        text: 'Usage & ROI dashboard',
        link: isRoleAdmin
          ? `/usage-dashboard?selectedTeamId=${selectedTeamUUID === '-1' ? teamAdminTeamIds[0] : selectedTeamUUID}`
          : `/usage-dashboard`,
        icon: faChartBar,
        hidden: (!isSA && !isRoleAdmin) || !rd1203ExecutiveDashboard,
        includeSeparator: false,
      },
    ]

    return menuItems.filter((item) => !item.hidden)
  }, [user, selectedTeamData.id, linkPrefix, rd1203ExecutiveDashboard])

  return (
    <DropdownMenu open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
      <Tooltip content='Settings' sideOffset={-2} withArrow>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid='admin-menu-button'
            aria-label='Admin menu'
            variant='ghost'
            size='icon'
            onClick={() => setIsOpen(true)}
          >
            <Icon icon={faGear} color={IconColor.darkPrimary} />
          </Button>
        </DropdownMenuTrigger>
      </Tooltip>
      <DropdownMenuContent className='tw-space-y-2 tw-mr-4'>
        {menuItems.map((item, index) => (
          <Fragment key={index}>
            <DropdownMenuItem className='tw-cursor-pointer tw-text-neutral-700'>
              <Link
                data-testid={`menu-link-${item.text
                  .toLowerCase()
                  .replace(/\s/g, '-')}`}
                href={item.link}
                onClick={() => {
                  setIsOpen(false)

                  trackFSEvent(FS.ADMIN_MENU_ITEM_CLICKED, {
                    item: item.text,
                    link: item.link,
                  })
                }}
              >
                <Icon
                  icon={item.icon}
                  color={IconColor.darkPrimary}
                  className='tw-mr-2'
                  aria-hidden='true'
                />
                {item.text}
              </Link>
            </DropdownMenuItem>
            {item.includeSeparator && <DropdownMenuSeparator />}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
